import React from 'react';
import { Gamepad2, Loader2 } from 'lucide-react';
import type { TransactionAllocation } from '../../../types';
import { cn } from '../../../utils';
import { useFormatDate } from '../../../hooks/useFormatDate';
import {
  formatCreditsAmount,
  getAllocationChildStatusDisplay,
} from '../../../utils/transactionDisplayUtils';
import type { AllocationFulfillmentSummary } from '../../../utils/transactionAllocationSummary';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/shadcn/table';

type Props = {
  allocations: TransactionAllocation[];
  summary: AllocationFulfillmentSummary | null;
  loading?: boolean;
  className?: string;
};

function AllocationStatusBadge({ status }: { status: string }) {
  const display = getAllocationChildStatusDisplay(status);
  return (
    <span
      className={cn(
        'inline-block rounded px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap',
        display.className,
      )}
    >
      {display.label}
    </span>
  );
}

const TransactionAllocationsPanel: React.FC<Props> = ({
  allocations,
  summary,
  loading = false,
  className,
}) => {
  const fmt = useFormatDate();

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-xs font-semibold flex items-center gap-1.5">
          <Gamepad2 className="h-3.5 w-3.5" />
          Game allocations
          {summary?.total ? (
            <span className="font-normal text-muted-foreground">({summary.total})</span>
          ) : null}
        </h4>
        {loading ? (
          <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading…
          </span>
        ) : null}
      </div>

      {summary && summary.fulfillmentStatus && summary.fulfillmentStatus !== 'completed' ? (
        <div className="rounded-md border border-amber-200 bg-amber-50/80 px-3 py-2 text-[11px] text-amber-950">
          <span className="font-semibold">Fulfillment: </span>
          {summary.completed}/{summary.total} completed
          {summary.refunded + summary.failed + summary.rejected > 0 ? (
            <span>
              {' '}
              · {summary.refunded + summary.failed + summary.rejected} failed
              {summary.refunded > 0 ? ' (refunded)' : ''}
            </span>
          ) : null}
          {summary.pending > 0 ? <span> · {summary.pending} pending</span> : null}
        </div>
      ) : null}

      <div className="rounded-md border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="h-8 text-xs">Game</TableHead>
              <TableHead className="h-8 text-xs">Status</TableHead>
              <TableHead className="h-8 text-xs text-right">Credits</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {allocations.map((allocation) => {
              const gameLabel =
                allocation.gameProviderName ||
                allocation.gameProviderCode ||
                allocation.gameProviderId?.slice(0, 8) ||
                'Game';
              const displayStatus = allocation.displayStatus || allocation.status;
              const amount = Math.abs(Number(allocation.amount || 0));
              const showError =
                allocation.errorMessage &&
                ['failed', 'rejected', 'refunded'].includes(String(displayStatus).toLowerCase());

              return (
                <TableRow key={allocation.id || allocation.transactionId}>
                  <TableCell className="py-2 align-top text-xs">
                    <div className="font-medium">{gameLabel}</div>
                    {allocation.gameProviderCode && allocation.gameProviderName ? (
                      <div className="text-[10px] text-muted-foreground font-mono">
                        {allocation.gameProviderCode}
                      </div>
                    ) : null}
                    <div
                      className="mt-0.5 font-mono text-[10px] text-muted-foreground truncate max-w-[14rem]"
                      title={allocation.transactionId}
                    >
                      #{allocation.id} · {allocation.transactionId}
                    </div>
                    {showError ? (
                      <div className="mt-1 text-[11px] text-red-600 whitespace-pre-wrap leading-snug">
                        {allocation.errorMessage}
                      </div>
                    ) : null}
                    {allocation.refundTransactionId ? (
                      <div className="mt-0.5 text-[10px] text-slate-600">
                        Refund: {allocation.refundTransactionId}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="py-2 align-top">
                    <AllocationStatusBadge status={displayStatus} />
                    {allocation.created_at ? (
                      <div className="mt-1 text-[10px] text-muted-foreground tabular-nums">
                        {fmt(allocation.created_at, { second: undefined })}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="py-2 text-right align-top text-xs font-medium tabular-nums text-green-600">
                    +{formatCreditsAmount(amount)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default TransactionAllocationsPanel;
